(function () {
  const QUIZ_QUESTIONS = [
    {
      id: 'bedtime',
      type: 'single',
      title: 'What time do you usually get into bed?',
      helper: 'A quick warm-up to understand your routine.',
      options: [
        { label: 'Before 10 PM', score: {} },
        { label: '10 – 11:30 PM', score: {} },
        { label: '11:30 PM – 1 AM', score: { overstim: 1 } },
        { label: 'After 1 AM', score: { overstim: 2 } },
      ],
    },
    {
      id: 'pattern',
      type: 'single',
      title: 'What actually happens most nights?',
      helper: 'This is the strongest signal in the quiz.',
      options: [
        { label: 'I lie awake for ages before falling asleep', score: { overstim: 3 } },
        { label: 'I fall asleep fine, but wake up at 2–4 AM', score: { waker: 3 } },
        { label: "I sleep the hours, but wake up feeling like I didn't", score: { tired: 3 } },
        { label: 'A bit of all three', score: { overstim: 1, waker: 1, tired: 1 } },
      ],
    },
    {
      id: 'phone',
      type: 'single',
      title: "In the last hour before bed, where's your phone?",
      helper: 'Late screen input is one of the biggest routine disruptors.',
      options: [
        { label: 'In my hand, right until I sleep', score: { overstim: 2 } },
        { label: 'I put it down 15–30 min before', score: { overstim: 1 } },
        { label: 'Out of the room / not an issue', score: {} },
      ],
    },
    {
      id: 'caffeine',
      type: 'single',
      title: 'Any caffeine after 4 PM?',
      helper: 'Tea counts too.',
      options: [
        { label: 'Most days (coffee or tea)', score: { waker: 2 } },
        { label: 'Occasionally', score: { waker: 1 } },
        { label: 'Rarely or never', score: {} },
      ],
    },
    {
      id: 'tried',
      type: 'multi',
      title: 'What have you already tried for sleep?',
      helper: 'Choose all that apply.',
      options: [
        { label: 'Melatonin (gummies or tablets)', melatonin: true },
        { label: 'Other sleep supplements' },
        { label: 'Sleep tracking apps / routine changes' },
        { label: 'Nothing yet', score: { overstim: 1, waker: 1, tired: 1 } },
      ],
    },
    {
      id: 'stress',
      type: 'single',
      title: 'How would you describe the last month?',
      helper: 'Stress load often shows up as a sleep pattern long before anything else.',
      options: [
        { label: 'Genuinely stressful — work or personal', score: { overstim: 3, tired: 1 } },
        { label: 'Busy but manageable', score: { overstim: 1 } },
        { label: 'Fairly settled', score: {} },
      ],
    },
  ];

  const QUIZ_PROFILES = {
    overstim: {
      label: 'Your result',
      title: 'The Overstimulated Mind',
      summary: "Your body is ready. Your mind isn't.",
      body:
        "You're likely going to bed physically tired but mentally still switched on. Stress and late input can keep your system in alert mode, so the wind-down never really starts.",
      tips: [
        'Put your phone in another room 45 minutes before bed.',
        "Write tomorrow's top 3 tasks on paper before bed to stop the mental loop.",
        'Keep the same bedtime for 7 nights, including weekends.',
      ],
      fit:
        'Deep Sleep Stack fits this profile by supporting a calmer wind-down. Magnesium helps the nervous system settle, while ashwagandha helps reduce the stress load underneath.',
    },
    waker: {
      label: 'Your result',
      title: 'The 3AM Waker',
      summary: 'Falling asleep is fine. Staying asleep is the issue.',
      body:
        'Mid-night waking often points to something interrupting the second half of sleep, like late caffeine, a late dinner, or a stress rhythm that peaks too early.',
      tips: [
        'Cut off caffeine by 2 PM for a week, including tea.',
        'Finish dinner about 3 hours before bed.',
        "If you wake up, don't check your phone. The light restarts the alertness cycle.",
      ],
      fit:
        "Deep Sleep Stack supports this profile by focusing on overnight continuity, not just sleep onset. That's often a better fit than a melatonin-style quick fix.",
    },
    tired: {
      label: 'Your result',
      title: 'The Wired-but-Tired Sleeper',
      summary: "You're getting the hours, but not the recovery.",
      body:
        'Time in bed is not the same as restorative sleep. Stress, inconsistent timing, and low daylight exposure can all reduce sleep quality even when the clock looks decent.',
      tips: [
        'Get 10–15 minutes of daylight within an hour of waking.',
        'Keep a fixed wake time, even on weekends.',
        'Watch alcohol, which can make you fall asleep faster but recover worse.',
      ],
      fit:
        'Deep Sleep Stack is more about stress support than sedation here, with magnesium supporting deeper overnight recovery and ashwagandha addressing the stress load.',
    },
  };

  const MELATONIN_OVERLAY = {
    body:
      "You've tried melatonin. Worth knowing why it may have stopped working: melatonin is a timing signal, not a true solution for an overstimulated nervous system or unmanaged stress. That's why Deep Sleep Stack takes a different route.",
  };

  function shouldAutoStart() {
    try {
      return new URLSearchParams(window.location.search).get('start') === '1';
    } catch (e) {
      return false;
    }
  }

  function trackQuizEvent(name, params) {
    const payload = Object.assign(
      {
        quiz_name: 'sleep_quiz',
        page_path: window.location.pathname,
      },
      params || {}
    );

    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
      return;
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: name }, payload));
    }
  }

  function initQuiz(root) {
    if (!root || root.dataset.quizReady === 'true') return;
    root.dataset.quizReady = 'true';

    const configEl = root.querySelector('[data-sleep-quiz-config]');
    const config = configEl ? JSON.parse(configEl.textContent) : {};

    const views = {
      intro: root.querySelector('[data-quiz-view="intro"]'),
      quiz: root.querySelector('[data-quiz-view="quiz"]'),
      result: root.querySelector('[data-quiz-view="result"]'),
    };

    const progressCopy = root.querySelector('[data-quiz-progress-copy]');
    const progressPct = root.querySelector('[data-quiz-progress-pct]');
    const progressBar = root.querySelector('[data-quiz-progress-bar]');
    const dotsWrap = root.querySelector('[data-quiz-dots]');
    const questionTitle = root.querySelector('[data-quiz-question-title]');
    const questionHelper = root.querySelector('[data-quiz-question-helper]');
    const optionsWrap = root.querySelector('[data-quiz-options]');
    const continueBtn = root.querySelector('[data-quiz-continue]');
    const backBtn = root.querySelector('[data-quiz-back]');

    const resultLabel = root.querySelector('[data-quiz-result-label]');
    const resultTitle = root.querySelector('[data-quiz-result-title]');
    const resultSummary = root.querySelector('[data-quiz-result-summary]');
    const resultBody = root.querySelector('[data-quiz-result-body]');
    const resultTips = root.querySelector('[data-quiz-result-tips]');
    const resultFit = root.querySelector('[data-quiz-result-fit]');
    const resultOverlay = root.querySelector('[data-quiz-result-overlay]');
    const resultOverlayBody = root.querySelector('[data-quiz-result-overlay-body]');
    const couponCode = root.querySelector('[data-quiz-coupon-code]');
    const couponCopyBtn = root.querySelector('[data-quiz-coupon-copy]');
    const couponFeedback = root.querySelector('[data-quiz-coupon-feedback]');
    const shopBtn = root.querySelector('[data-quiz-shop-link]');
    const retakeBtn = root.querySelector('[data-quiz-retake]');
    const startBtn = root.querySelector('[data-quiz-start]');

    const state = {
      step: -1,
      answers: new Array(QUIZ_QUESTIONS.length).fill(null),
      hasTrackedStart: false,
      hasTrackedComplete: false,
      couponResetTimer: null,
    };

    if (dotsWrap) {
      dotsWrap.innerHTML = QUIZ_QUESTIONS.map(() => '<span class="sleep-quiz__dot"></span>').join('');
    }

    function showView(name) {
      Object.keys(views).forEach((key) => {
        if (!views[key]) return;
        views[key].classList.toggle('is-active', key === name);
      });
    }

    function updateProgress() {
      const current = state.step + 1;
      const pct = Math.round((current / QUIZ_QUESTIONS.length) * 100);
      if (progressCopy) progressCopy.textContent = current + ' of ' + QUIZ_QUESTIONS.length;
      if (progressPct) progressPct.textContent = pct + '%';
      if (progressBar) progressBar.style.transform = 'scaleX(' + pct / 100 + ')';

      if (dotsWrap) {
        Array.from(dotsWrap.children).forEach((dot, index) => {
          dot.classList.toggle('is-current', index === state.step);
          dot.classList.toggle('is-done', index < state.step);
        });
      }
    }

    function isAnswered(question, answer) {
      if (question.type === 'multi') {
        return Array.isArray(answer) && answer.length > 0;
      }
      return typeof answer === 'number';
    }

    function renderQuestion() {
      const question = QUIZ_QUESTIONS[state.step];
      const answer = state.answers[state.step];

      showView('quiz');
      updateProgress();
      questionTitle.textContent = question.title;
      questionHelper.textContent = question.helper || '';
      optionsWrap.innerHTML = '';
      optionsWrap.setAttribute('aria-label', question.title);

      question.options.forEach(function (option, index) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sleep-quiz__option' + (question.type === 'multi' ? ' is-multi' : '');
        button.textContent = option.label;

        const selected =
          question.type === 'multi'
            ? Array.isArray(answer) && answer.includes(index)
            : answer === index;

        if (selected) button.classList.add('is-selected');

        button.addEventListener('click', function () {
          if (question.type === 'multi') {
            const current = Array.isArray(state.answers[state.step]) ? state.answers[state.step].slice() : [];
            const exists = current.indexOf(index) !== -1;
            var nextSelection = exists ? current.filter(function (item) { return item !== index; }) : current.concat([index]);

            if (!exists && question.id === 'tried') {
              var nothingYetIndex = -1;
              for (var i = 0; i < question.options.length; i++) {
                if (question.options[i].label === 'Nothing yet') {
                  nothingYetIndex = i;
                  break;
                }
              }
              if (index === nothingYetIndex) {
                nextSelection = [index];
              } else if (nothingYetIndex !== -1) {
                nextSelection = nextSelection.filter(function (item) { return item !== nothingYetIndex; });
              }
            }

            state.answers[state.step] = nextSelection;
            trackQuizEvent('quiz_step_answer', {
              step_number: state.step + 1,
              question_id: question.id,
              answer_value: nextSelection
                .map(function (selectedIndex) {
                  return question.options[selectedIndex] ? question.options[selectedIndex].label : '';
                })
                .filter(Boolean)
                .join(' | '),
            });
            renderQuestion();
            return;
          }

          state.answers[state.step] = index;
          trackQuizEvent('quiz_step_answer', {
            step_number: state.step + 1,
            question_id: question.id,
            answer_value: option.label,
          });
          renderQuestion();
          window.setTimeout(goNext, 160);
        });

        optionsWrap.appendChild(button);
      });

      backBtn.hidden = state.step === 0;
      continueBtn.hidden = question.type !== 'multi';
      continueBtn.disabled = !isAnswered(question, answer);

      try {
        root.querySelector('.sleep-quiz__shell').scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {}
    }

    function scoreQuiz() {
      var totals = { overstim: 0, waker: 0, tired: 0 };
      var melatonin = false;

      QUIZ_QUESTIONS.forEach(function (question, questionIndex) {
        var answer = state.answers[questionIndex];
        if (!isAnswered(question, answer)) return;

        var indices = question.type === 'multi' ? answer : [answer];
        indices.forEach(function (optionIndex) {
          var option = question.options[optionIndex];
          if (!option) return;

          if (option.score) {
            Object.keys(option.score).forEach(function (key) {
              totals[key] += option.score[key];
            });
          }

          if (option.melatonin) melatonin = true;
        });
      });

      var maxScore = Math.max(totals.overstim, totals.waker, totals.tired);
      var priority = ['overstim', 'waker', 'tired'];
      var winner = 'overstim';
      for (var i = 0; i < priority.length; i++) {
        if (totals[priority[i]] === maxScore) {
          winner = priority[i];
          break;
        }
      }

      return { winner: winner, melatonin: melatonin };
    }

    function renderResult() {
      var result = scoreQuiz();
      var profile = QUIZ_PROFILES[result.winner];

      resultLabel.textContent = profile.label;
      resultTitle.textContent = profile.title;
      resultSummary.textContent = profile.summary;
      resultBody.textContent = profile.body;
      resultFit.textContent = profile.fit;
      resultTips.innerHTML = '';

      profile.tips.forEach(function (tip) {
        var item = document.createElement('li');
        item.textContent = tip;
        resultTips.appendChild(item);
      });

      if (result.melatonin) {
        resultOverlay.hidden = false;
        resultOverlayBody.textContent = MELATONIN_OVERLAY.body;
      } else {
        resultOverlay.hidden = true;
      }

      if (couponCode) couponCode.textContent = config.couponCode || 'NIGHTOWL';
      if (couponFeedback) {
        couponFeedback.innerHTML = 'Apply <strong>' + (config.couponCode || 'NIGHTOWL') + '</strong> at checkout.';
      }
      if (couponCopyBtn) couponCopyBtn.textContent = 'Copy code';

      if (shopBtn) shopBtn.href = config.productUrl || '/products/deep-sleep';

      if (!state.hasTrackedComplete) {
        trackQuizEvent('quiz_complete', {
          total_questions: QUIZ_QUESTIONS.length,
          result_profile: result.winner,
          melatonin_flag: result.melatonin ? 'true' : 'false',
        });
        trackQuizEvent('quiz_result_profile', {
          result_profile: result.winner,
        });
        if (result.melatonin) {
          trackQuizEvent('quiz_melatonin_flag', {
            result_profile: result.winner,
          });
        }
        state.hasTrackedComplete = true;
      }

      showView('result');
      try {
        root.querySelector('.sleep-quiz__shell').scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {}
    }

    function goNext() {
      if (state.step >= QUIZ_QUESTIONS.length - 1) {
        renderResult();
        return;
      }
      state.step += 1;
      renderQuestion();
    }

    function goBack() {
      if (state.step <= 0) return;
      state.step -= 1;
      renderQuestion();
    }

    function startQuiz() {
      state.step = 0;
      if (!state.hasTrackedStart) {
        trackQuizEvent('quiz_start', {
          entry_mode: shouldAutoStart() ? 'modal_to_page' : 'direct_page',
        });
        state.hasTrackedStart = true;
      }
      renderQuestion();
    }

    if (startBtn) startBtn.addEventListener('click', startQuiz);
    continueBtn.addEventListener('click', goNext);
    backBtn.addEventListener('click', goBack);
    retakeBtn.addEventListener('click', function () {
      state.step = -1;
      state.answers = new Array(QUIZ_QUESTIONS.length).fill(null);
      state.hasTrackedStart = false;
      state.hasTrackedComplete = false;
      trackQuizEvent('quiz_retake');
      showView('intro');
      try {
        root.querySelector('.sleep-quiz__shell').scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {}
    });

    if (shopBtn) {
      shopBtn.addEventListener('click', function () {
        var result = scoreQuiz();
        trackQuizEvent('quiz_shop_click', {
          result_profile: result.winner,
          destination: shopBtn.getAttribute('href') || '',
        });
      });
    }

    if (couponCopyBtn) {
      couponCopyBtn.addEventListener('click', function () {
        var code = config.couponCode || 'NIGHTOWL';

        function handleCopied() {
          couponCopyBtn.textContent = 'Copied';
          if (couponFeedback) {
            couponFeedback.innerHTML = 'Code <strong>' + code + '</strong> copied. Paste it at checkout.';
          }
          if (state.couponResetTimer) window.clearTimeout(state.couponResetTimer);
          state.couponResetTimer = window.setTimeout(function () {
            couponCopyBtn.textContent = 'Copy code';
            if (couponFeedback) {
              couponFeedback.innerHTML = 'Apply <strong>' + code + '</strong> at checkout.';
            }
          }, 2200);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(handleCopied, function () {});
          return;
        }

        try {
          var tempInput = document.createElement('input');
          tempInput.value = code;
          tempInput.setAttribute('readonly', '');
          tempInput.style.position = 'absolute';
          tempInput.style.left = '-9999px';
          document.body.appendChild(tempInput);
          tempInput.select();
          document.execCommand('copy');
          document.body.removeChild(tempInput);
          handleCopied();
        } catch (e) {}
      });
    }

    if (shouldAutoStart()) {
      startQuiz();
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-sleep-quiz]').forEach(initQuiz);
  });
})();
